import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

interface SidePot {
  config_type: 'individual_game' | 'game_series';
  game_identifier: string;
  winners_count: number;
  payout_amount: number;
}

interface Props {
  sideEventId: number;
  onClose: () => void;
  onSaved: () => void;
}

const BookieEditSidePotModal: React.FC<Props> = ({ sideEventId, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [appliesTo, setAppliesTo] = useState<'bowler' | 'doubles'>('bowler');
  const [cost, setCost] = useState(0);
  const [appliesForGameNumbers, setAppliesForGameNumbers] = useState('');
  const [sidePots, setSidePots] = useState<SidePot[]>([]);

  useEffect(() => {
    axios
      .get(`${API}/bookie-league-management/side-events/${sideEventId}/config`, { headers: authHeader() })
      .then(({ data }) => {
        setName(data.name || '');
        setAppliesTo(data.applies_to || 'bowler');
        setCost(Number(data.cost) || 0);
        setAppliesForGameNumbers(data.applies_to_game_numbers || '');
        setSidePots(
          (data.side_pots || []).map((p: any) => ({
            config_type: p.config_type || 'individual_game',
            game_identifier: p.game_identifier || '',
            winners_count: Number(p.winners_count) || 1,
            payout_amount: Number(p.payout_amount) || 0,
          }))
        );
      })
      .catch(() => setError('Failed to load configuration'))
      .finally(() => setLoading(false));
  }, [sideEventId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required'); return; }
    if (sidePots.length === 0) { setError('At least one pot is required'); return; }
    setSaving(true);
    setError('');
    try {
      await axios.put(
        `${API}/bookie-league-management/side-events/${sideEventId}/config`,
        { name: name.trim(), applies_to: appliesTo, cost, applies_to_game_numbers: appliesForGameNumbers, side_pots: sidePots },
        { headers: authHeader() }
      );
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    'w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500';
  const labelCls = 'block text-sm font-medium text-gray-300 mb-1';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">Edit Side Pot Configuration</h2>
            <p className="text-xs text-gray-400 mt-0.5">Update pots, payouts, and settings</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Row 1: Name + Applies To */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Name *</label>
                <input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className={inputCls}
                  placeholder="e.g. High Game Pot"
                />
              </div>
              <div>
                <label className={labelCls}>Applies To *</label>
                <select
                  value={appliesTo}
                  onChange={e => setAppliesTo(e.target.value as 'bowler' | 'doubles')}
                  className={inputCls}
                  style={{ colorScheme: 'dark' }}
                >
                  <option value="bowler" className="bg-gray-800">Individual Bowler</option>
                  <option value="doubles" className="bg-gray-800">Doubles</option>
                </select>
              </div>
            </div>

            {/* Row 2: Cost + Game Numbers */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Cost ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.50"
                  value={cost}
                  onChange={e => setCost(parseFloat(e.target.value) || 0)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Game Numbers</label>
                <input
                  value={appliesForGameNumbers}
                  onChange={e => setAppliesForGameNumbers(e.target.value)}
                  className={inputCls}
                  placeholder="e.g. 1,2,3"
                />
                <p className="text-xs text-gray-500 mt-1">Comma-separated (used for reference only here)</p>
              </div>
            </div>

            {/* Side Pots Table */}
            <div className="bg-gray-700/40 border border-gray-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-purple-300">Side Pots</h3>
                <button
                  type="button"
                  onClick={() =>
                    setSidePots(prev => [
                      ...prev,
                      { config_type: 'game_series', game_identifier: appliesForGameNumbers || '1,2,3', winners_count: 1, payout_amount: 0 },
                    ])
                  }
                  className="text-xs px-2.5 py-1 bg-blue-700/60 hover:bg-blue-700 text-white rounded-lg"
                >
                  + Add Series Pot
                </button>
              </div>

              {sidePots.length === 0 && (
                <p className="text-xs text-gray-500">No pots configured. Add a series pot above.</p>
              )}

              {sidePots.length > 0 && (
                <div className="grid grid-cols-[1fr_1fr_100px_28px] gap-2 text-[10px] text-gray-500 uppercase tracking-wide mb-1 px-1">
                  <span>Pot</span>
                  <span>Top % Winners</span>
                  <span>Payout $</span>
                  <span />
                </div>
              )}

              <div className="space-y-2">
                {sidePots.map((pot, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_100px_28px] gap-2 text-sm items-center">
                    {pot.config_type === 'game_series' ? (
                      <input
                        value={pot.game_identifier}
                        onChange={e =>
                          setSidePots(prev => prev.map((p, idx) => idx === i ? { ...p, game_identifier: e.target.value } : p))
                        }
                        placeholder="Games e.g. 1,2,3"
                        className={inputCls + ' text-sm py-1.5'}
                      />
                    ) : (
                      <div className="bg-gray-700 px-3 py-1.5 rounded text-gray-300 text-sm">
                        Game {pot.game_identifier}
                      </div>
                    )}
                    <select
                      value={pot.winners_count}
                      onChange={e =>
                        setSidePots(prev => prev.map((p, idx) => idx === i ? { ...p, winners_count: +e.target.value } : p))
                      }
                      className={inputCls + ' text-sm py-1.5'}
                      style={{ colorScheme: 'dark' }}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(v => (
                        <option key={v} value={v} className="bg-gray-800">{v * 10}%</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      placeholder="0"
                      value={pot.payout_amount}
                      onChange={e =>
                        setSidePots(prev => prev.map((p, idx) => idx === i ? { ...p, payout_amount: +e.target.value } : p))
                      }
                      className={inputCls + ' text-sm py-1.5'}
                    />
                    <button
                      type="button"
                      onClick={() => setSidePots(prev => prev.filter((_, idx) => idx !== i))}
                      className="text-gray-500 hover:text-red-400 p-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {error && (
              <div className="bg-red-900/40 border border-red-500 text-red-300 text-sm p-3 rounded-lg">{error}</div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium text-white transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2.5 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 rounded-lg text-sm font-bold text-white transition-colors flex items-center gap-2"
              >
                {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BookieEditSidePotModal;
